import { QueryResponse, ResultInfo } from '../types/ResultTypes';
import IConversationHistory from '../models/iConversationHistory';
import IUserMessage from '../models/iUserMessage';
import IBotMessage from '../models/iBotMessage';
import IConversationHistoryDto from '../models/iConversationHistoryDto';
import axios from 'axios';
import { API_BASE_URL, ContentTypes, getAxiosRequestConfig } from './apiBase';
import { GeminiResponse } from '../models/iGeminiResponse';
import { SearchTopic, SearchTopics } from '../enum/SearchTopics';

/**
 *
 * @param bearerToken
 * @param text - The user query
 * @param topic - The topic/intent to bypass the classifier
 * @returns
 */
export const queryLlm = async (
  bearerToken: string,
  text: string,
  topic: SearchTopic,
  turn: number,
  parentId?: string,
  previous_results?: ResultInfo[]
): Promise<QueryResponse> => {
  const response =
    topic === SearchTopics.MaterialSearch
      ? await axios.post<QueryResponse>(
          `${API_BASE_URL}/Chatbot/query`,
          {
            text,
            topic,
            turn,
            parentId,
            materialInfoList: previous_results,
          },
          getAxiosRequestConfig(bearerToken)
        )
      : await axios.post<QueryResponse>(
          `${API_BASE_URL}/Chatbot/query`,
          {
            text,
            topic,
            turn,
            parentId,
            productInfoList: previous_results,
          },
          getAxiosRequestConfig(bearerToken)
        );
  return response.data;
};

/**
 * User Gemini to answer the user query
 * @param bearerToken
 * @param text - The user query
 * @returns
 */
export const queryGemini = async (bearerToken: string, text: string): Promise<GeminiResponse> => {
  const response = await axios.get<GeminiResponse>(
    `${API_BASE_URL}/Chatbot/queryGemini/${text}`,
    getAxiosRequestConfig(bearerToken)
  );

  return response.data;
};

/**
 * Gets the details of a past conversation that has the given conversationId
 * @param bearerToken
 * @param numRowsToRetrieve the number of recent conversations to get
 * @returns
 */
export const getUserConversations = async (
  bearerToken: string,
  numRowsToRetrieve: number
): Promise<IConversationHistory[]> => {
  return await axios
    .get<IConversationHistoryDto[]>(
      `${API_BASE_URL}/Chatbot/userConversations/${numRowsToRetrieve}`,
      getAxiosRequestConfig(bearerToken)
    )
    .then((response) => {
      const parsed: IConversationHistory[] = response.data.map((conversation) => {
        return {
          id: conversation.id,
          userId: conversation.userId,
          createdTime: conversation.createdTime,
          conversationData: JSON.parse(conversation.conversationData),
        };
      });
      return parsed;
    });
};

/**
 * Create a new conversation history
 * @param bearerToken
 * @param messages
 * @returns
 */
export const createUserConversations = async (
  bearerToken: string,
  messages: (IUserMessage | IBotMessage)[]
): Promise<IConversationHistory> => {
  const messageJson = JSON.stringify(messages);
  const conversationHistory = await axios
    .post<IConversationHistoryDto>(
      `${API_BASE_URL}/Chatbot/userConversation`,
      { messageJson },
      getAxiosRequestConfig(bearerToken)
    )
    .then((response) => {
      return {
        id: response.data.id,
        userId: response.data.userId,
        createdTime: response.data.createdTime,
        conversationData: JSON.parse(response.data.conversationData),
      };
    });
  return conversationHistory;
};

/**
 * Updates the given conversation with the new messages
 * @param bearerToken
 * @param conversationId
 * @param messages
 * @returns
 */
export const updateUserConversations = async (
  bearerToken: string,
  conversationId: string,
  messages: (IUserMessage | IBotMessage)[]
): Promise<void> => {
  const messageJson = JSON.stringify(messages);
  const response = await axios.put<void>(
    `${API_BASE_URL}/Chatbot/userConversation/${conversationId}`,
    { messageJson },
    getAxiosRequestConfig(bearerToken)
  );
  return response.data;
};

/**
 *
 * @param bearerToken
 * @param document
 * @returns
 */
export const chatWithDocument = async (bearerToken: string, document: File, userQuery: string): Promise<string> => {
  const formData = new FormData();
  formData.append('file', document);
  formData.append('userQuery', userQuery);

  const response = await axios.post<string>(
    `${API_BASE_URL}/Chatbot/documentChat`,
    formData,
    getAxiosRequestConfig(bearerToken, undefined, ContentTypes.FormData)
  );
  return response.data;
};

/**
 *
 * @param bearerToken
 * @param bNumber
 * @param userQuery
 * @returns
 */
export const chatWithTds = async (
  bearerToken: string,
  bNumber: string,
  isInDistillery: boolean,
  userQuery: string
): Promise<string> => {
  const response = await axios.get<string>(
    `${API_BASE_URL}/Chatbot/tdsChat`,
    getAxiosRequestConfig(bearerToken, { bNumber, isInDistillery, userQuery }, ContentTypes.Plaintext)
  );
  return response.data;
};
